import { LowerCasePipe } from '@angular/common';
import { Component, computed, effect, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { TitleComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/title/title.component';
import { IKpiCard } from '@cta-analytics-details/cta-analytics-details-kpis-tile/cta-analytics-details-kpi-card/cta-analytics-details-kpi-card.interface';
import { DeliverableFindingsStore } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-findings.store';
import { EDeliverableType } from '@enums/campaign.enum';
import { ITitleAnalytics } from '@interfaces/deliverable-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { AnalyticsDetailsUtilitiesService } from '@utilities/analytics-details-utilities.service';
import { TextPostAnalyticsBaseDirective } from '../text-post-analytics-base/text-post-analytics-base.directive';

@Component({
  imports: [TranslateModule, MatButtonModule, TitleComponent, LowerCasePipe],
  providers: [DeliverableFindingsStore],
  selector: 'app-title-post-analytics',
  templateUrl: './title-post-analytics.component.html',
})
export class TitlePostAnalyticsComponent extends TextPostAnalyticsBaseDirective<undefined, ITitleAnalytics> {
  public override readonly deliverableType = EDeliverableType.title;
  public override readonly kpiCardData: IKpiCard = {
    icon: 'view.svg',
    id: 'title-count',
    title: this.translate.instant('pages.campaignDetails.ctaAnalytics.details.talkingPoint.totalCount'),
    value: 0,
  };

  public override readonly keywordCardList = signal<IKpiCard[]>([]);

  public override readonly currentDeliverable = computed(() => {
    const deliverableId = this.deliverableId();
    const executionAnalytics = this.executionAnalyticsWithFindings();

    if (deliverableId && executionAnalytics) {
      return executionAnalytics?.ctaAnalytics?.titles?.find((c) => c.deliverableId === deliverableId);
    }

    return null;
  });

  public override readonly contentData = computed(() => {
    const socialContent = this.executionAnalyticsWithFindings()?.content?.socialContent;

    if (!socialContent) {
      return null;
    }

    if ('youtubeData' in socialContent) {
      return socialContent.youtubeData;
    }

    return null;
  });

  constructor() {
    super();

    this.initializeEffect();
    this.setKeywordsEffect();
  }

  protected override setKeywordCounts(): void {
    const title = this.currentDeliverable();

    const keywords = title?.keywords?.map((k) => k.keyword) ?? [];
    const titleText = this.contentData()?.title ?? '';
    this.keywordCounts = AnalyticsDetailsUtilitiesService.getIndividualKeywordCounts(keywords, [titleText]);

    const total = Object.values(this.keywordCounts).reduce((sum, val) => sum + val, 0);
    this.kpiCardData.value = total;

    AnalyticsDetailsUtilitiesService.mapKeywordCountsToDeliverableForm(this.deliverableForm(), this.keywordCounts);
  }

  protected override setKeywordCardList(): void {
    const title = this.currentDeliverable();

    if (title?.keywords && title.keywords.length) {
      const kpiCardList = title.keywords.map((k) => {
        return {
          icon: 'keyword.svg',
          id: `${k.keyword}-kpi-card`,
          keyword: k.keyword,
          value: this.keywordCounts[k.keyword] ?? 0,
        };
      });

      this.keywordCardList.set(kpiCardList);
    }
  }

  protected override setKeywordsEffect(): void {
    effect(() => {
      const deliverableId = this.deliverableId();
      const executionAnalytics = this.executionAnalyticsWithFindings();

      if (deliverableId && executionAnalytics) {
        this.keywords = this.currentDeliverable()?.keywords?.map((k) => k.keyword) ?? [];
      }
    });
  }
}
